<template>
	<component
		:is="isModalProvider ? 'button' : ClickoutAnchor"
		:rel="isSponsoredOffer"
		:referrerpolicy="shouldMaskClicks ? 'no-referrer' : undefined"
		v-bind="isModalProvider ? {} : { href: hideOfferUrl ? {} : offerUrl }"
		clickoutType="regular"
		target="_blank"
		class="offer"
		@click="isModalProvider ? openModal() : handleClickOut()"
		v-observe-visibility="observeVisibilityOptions"
	>
		<span class="picture-wrapper" :class="{ 'picture-wrapper--jwtv-v4': isJwtvV4 }">
			<span v-if="isProviderPromo && providerPromo?.ribbon" class="ribbon">{{ providerPromo.ribbon }}</span>

			<ProviderIconSquare :provider="offer.package" loading="eager" />
		</span>

		<div class="offer__label" :class="{ promoted }">
			<slot>
				<span>{{ offerLabel }}</span>
			</slot>

			<span v-show="!hidePresentationTypeLabel && presentationTypeLabel" class="offer__label--presentation">
				{{ presentationTypeLabel }}
			</span>

			<span v-if="!hideLeavingShowIcon && isLeavingIconShown" class="offer__label--leaving-icon">
				<FontAwesomeIcon slot="icon" :icon="faClock" />
			</span>
		</div>
	</component>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';

import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { diffInDays } from '@/helpers/date-helper';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { BuyboxParam, getTranslatableForOfferGraphql, OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { MonetizationType, PresentationType, ObjectType } from '@/@types/graphql-types';

import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';

import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIconSquare from '@/features/ui/components/ProviderIconSquare.vue';
import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { toValue, type MaybeRef } from 'vue';

import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import { useProviderPromotion } from '@/helpers/provider-promotion';
import { faClock } from '@fortawesome/free-solid-svg-icons';

import { useAppStore } from '@/stores/pinia/useAppStore';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { useGridBuyboxRedesignCTAExp } from '@/components/experiments/GridBuyboxRedesignExps/GridBuyboxRedesignCTAExp';
import { useGridMoreInfoExp } from '@/components/experiments/GridBuyboxRedesignExps/GridMoreInfoExp';
import { useMounted } from '@vueuse/core';
import { usePrimePlayPromoExp } from '@/components/experiments/PrimePlayPromoExp';
import { useExperiment } from '@/libraries/experiment';

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
};

interface BuyBoxOfferProps {
	/**  Bundles get a different iconUrl, this property is added to the offer in OfferRow */
	offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
	promoted?: boolean;
	objectType: ObjectType;
	hidePresentationTypeLabel?: boolean;
	title?: TitleDetail;
	hideLeavingShowIcon?: boolean;
	additionalContexts?: SnowplowContext[];
	inline?: boolean;
}

const props = withDefaults(defineProps<BuyBoxOfferProps>(), {
	promoted: false,
	hidePresentationTypeLabel: false,
	hideLeavingShowIcon: false,
	additionalContexts: () => [],
});

const emit = defineEmits<{
	(
		e: 'clickOut',
		data: {
			buyboxOffer: BuyBoxOfferProps['offer'];
			isBundle: boolean;
			isLeavingIconShown: boolean;
		}
	): void;
}>();

const { country, webLocale } = storeToRefs(useLanguageStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !props.offer.bundleId && isPromotionOffer(props.offer));

const isModalProvider = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(props.offer.package.technicalName)
);

const _clickoutType = computed(() => {
	if (isModalProvider.value) return 'video_player';
	return props.promoted ? 'promoted' : 'regular';
});
const { clickoutType, placement } = getClickoutTrackingPlacement(_clickoutType.value);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);

const shouldMaskClicks = computed(() => shouldClickMask(props.offer.package.packageId, country.value));

const isBannerBuyBox = inject('isBannerBuyBox', false);
const isMiddleBuyBox = inject('isMiddleBuyBox', false);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const titleSourceId = inject<string | null>('titleSourceId', null);
const noOffer = inject<boolean | undefined>('noOffer', undefined);

const jwRawEntityId = computed(() => episodeEntityId ?? props.title?.id);

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage({ entityId: jwRawEntityId });

const offerLocation = computed(() => {
	if (toValue(isBannerBuyBox)) return OfferLocation.BANNER_BUYBOX;
	if (toValue(isMiddleBuyBox)) return OfferLocation.MIDDLE_BUYBOX;
	if (props.promoted) return OfferLocation.PROMOTED_BUYBOX;

	return undefined;
});
const { setPrimePlayPromoTags } = usePrimePlayPromoExp();

const buyboxIndex = inject<MaybeRef<number | undefined>>('buyboxIndex', undefined);

// the UCT offer url is massive.
// in some cases we have 300 links on the page that explodes the HTML.
// if we exceed 2mb page size, google will stop the crawl - so it's important to stay under 2mb.
//
// currently we only hide the offer url:
// - during SSR
// - on guide article pages
// - NOT on the 1st article item
const route = useRoute();
const isMounted = useMounted();
const hideOfferUrl = computed(
	() =>
		['app.editorial.subdomain.article'].includes(route.name as string) &&
		!isMounted.value &&
		(toValue(buyboxIndex) || 0) >= 1
);

const isLeavingIconShown = computed(() => {
	return (
		!props.promoted &&
		(isLoggedIn.value as boolean) &&
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30
	);
});

function clickoutBase(url: ClickoutUrl) {
	return url
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext(
			'ClickoutContextGraphql',
			'contentType',
			isLeavingIconShown.value ? ClickoutContentType.LeavingSoon : undefined
		)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'rowNumber', toValue(buyboxIndex))
		.set('uct_country', country.value)
		.set('uct_buybox', props.promoted ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.setTitleSourceId(titleSourceId)
		.setCanary(xServeTrack.value)
		.setGenre(props.title?.content.genres)
		.setPageType(props.title?.objectType);
}

const { setGridRedesignTags } = useGridBuyboxRedesignCTAExp();
const { setGridMoreInfoTags } = useGridMoreInfoExp();

const { activeVariant: jwtvCinematicVariant } = useExperiment('EXP_JWTV_TVOD_CINEMATIC_V2');
const isJwtvV4 = computed(
	() => props.offer.package.technicalName === 'justwatchtv' && jwtvCinematicVariant.value === 'variant_4'
);

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(props.offer);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...props.offer, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(props.offer.standardWebURL!);
		const offer = { ...props.offer, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(props.offer.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: () => props.offer,
			title: () => props.title,
			placement: 'ro',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const url = clickoutUrl
		.apply(clickoutBase)
		.apply(u => setGridRedesignTags(u, 'ro', props.offer.package.shortName, props.inline))
		.apply(u => setGridMoreInfoTags(u, 'ro', props.offer.package.shortName, props.inline));

	url.apply(url =>
		setPrimePlayPromoTags(url, 'ro', props.offer.package.shortName, props.offer.package.technicalName)
	);

	if (titleContext.value) {
		url.addContexts(titleContext.value).updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value);
	}

	// Amazon streaming bounty redirects

	if ([9, 119].includes(props.offer.package.packageId)) {
		return url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const offerLabel = computed(() => {
	if (isProviderPromo.value && providerPromo.value?.offerText) return providerPromo.value.offerText;

	const translation = getTranslatableForOfferGraphql(props.offer, props.objectType);

	if (!translation) return props.offer.retailPrice;

	if (props.offer.monetizationType === MonetizationType.FlatrateAndBuy) {
		return translation + (props.offer?.retailPrice ?? '');
	}

	return translation;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
		case PresentationType.Bluray_4K:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

/* OFFER IMPRESSION TRACKING */
const observeVisibilityOptions = computed(() => {
	return {
		...observableOptions,
		callback: onVisibilityChanged,
	};
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);
}

function handleClickOut() {
	trackTagEvent(() => props.offer);

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.offer.iconUrl,
		isLeavingIconShown: isLeavingIconShown.value,
	});
}

//START AVOD
const { openRentalModal } = useRentalModal();
function openModal() {
	if (!isModalProvider.value) return;
	const modalData = playerModalByProvider[props.offer.package.technicalName];

	if (isJWTVRentalOffer(props.offer)) {
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(offerUrl.value);
		openRentalModal({
			offer: props.offer,
			title: props.title!,
			clickoutUrl: offerUrl.value,
		});
		return;
	}

	ModalHelper.openModal(
		modalData.cmp,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	trackClickout(offerUrl.value);
}
//END AVOD
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

button.offer {
	background: none;
	border: none;

	picture {
		display: inline-block;
	}
}

.offer {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	cursor: pointer;

	&__label {
		display: flex;
		align-items: center;
		margin: 0;
		text-align: center;
		font-size: 13px;
		text-decoration: none;
		color: $jw-color-lightgray;
		white-space: nowrap;

		&.promoted {
			font-weight: 700;
		}

		&--presentation {
			margin-inline-start: 3px;
			font-size: 9px;
			font-weight: 700;
			color: $jw-color-gold;
			text-transform: uppercase;
		}

		&--leaving-icon {
			color: #b2474d;
			margin-inline-start: 3px;
		}
	}
}

.picture-wrapper {
	position: relative;
	overflow: hidden;

	&--jwtv-v4 {
		border-radius: 8px;
		overflow: hidden;
		outline: 1px solid #fbc500;
	}
}

.ribbon {
	position: absolute;
	top: 5px;
	right: -12px;
	width: 100%;
	text-align: center;
	background-color: $jw-color-lightgray;
	color: $jw-color-darkgray;
	font-size: 9px;
	font-weight: 700;
	padding: 0px 4px;
	border-radius: 0 0 0 5px;
	z-index: 1;
	transform: rotate(45deg);
}
</style>
